import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import {
  NotifierType,
  SimpleMessage,
  showNotification,
} from '~/legacy/components';
import { usePricedProductsContext } from '~/legacy/easy-estimator-page/context/pricedProductsContext';
import { useAppDispatch } from '~/redux/hooks';
import {
  updateEeStatusList,
  updateIgnoreFetchedEstimate,
  updateIsNewEstimate,
  updateLoadEstimateCode,
} from '~/redux/slices/estimate';

interface UseExpiredEstimateActionsReturn {
  openToNewEstimate: () => void;
  createCloneEstimate: (translationSection: 'EXPIRED' | 'CLOSED') => void;
}

const useExpiredEstimateActions = (): UseExpiredEstimateActionsReturn => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { resetAllPricing } = usePricedProductsContext();

  const openToNewEstimate = useCallback(() => {
    const url = new URL(document.location.href);

    url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
    window.location.href = url.toString();
  }, []);

  const createCloneEstimate = useCallback(
    (translationSection: 'EXPIRED' | 'CLOSED') => {
      // 1. Mark that we should ignore the fetched estimate
      dispatch(updateIgnoreFetchedEstimate(true));

      // 2. Clear the load estimate code (treat as new estimate)
      dispatch(updateLoadEstimateCode(undefined));

      // 3. Mark as new estimate (will save with new code)
      dispatch(updateIsNewEstimate(true));

      // 4. Clear status list (removes NO_SAVE, allows saving)
      dispatch(updateEeStatusList([]));

      // 5. Force pricing recalculation with current material/color prices
      resetAllPricing();

      // 6. Update URL (remove loadEstimate param without page reload)
      const url = new URL(document.location.href);

      url.searchParams.delete(LOCAL_STORAGE_KEY.LOAD_ESTIMATE);
      window.history.replaceState({}, '', url.toString());

      // 7. Show follow-up notification
      const basePath = `NOTIFICATIONS.FETCH_ESTIMATE.${translationSection}.COPY_FOLLOWUP`;

      showNotification(
        NotifierType.WARNING,
        t(`${basePath}.TITLE`),
        <SimpleMessage
          description={t(`${basePath}.DESC`)}
          message={t(`${basePath}.MSG`)}
        />,
        '',
        0
      );
    },
    [dispatch, resetAllPricing, t]
  );

  return { openToNewEstimate, createCloneEstimate };
};

export default useExpiredEstimateActions;
